import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { getGoalBySlug } from 'Shared/selectors/Goals.selectors';

import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import RadioInput from 'Shared/components/RadioInput/RadioInput';
import GoalStatus from 'Shared/components/Goals/GoalStatus/GoalStatus';

import './CompleteGoalModal.scss';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type Props = {
  onToggle: () => void;
  onSubmit: Function;
  goalSlug: string;
  goalResolutionNote: string;
};

export default function CompleteGoalModal({ goalSlug, goalResolutionNote, onToggle, onSubmit }: Props) {
  const [formValues, setFormValues] = useState({ resolutionNote: goalResolutionNote || '', closeActionItems: true });
  const goal = useSelector(state => getGoalBySlug(state, { slug: goalSlug }))?.toJS();

  const handleResolutionNoteChange = event => setFormValues({ ...formValues, resolutionNote: event.target.value });

  const handleCompleteActionItemsClick = () => setFormValues({ ...formValues, closeActionItems: true });

  const handleKeepActionItemsClick = () => setFormValues({ ...formValues, closeActionItems: false });

  const handleCompleteClick = () => {
    onSubmit({ goalSlug, ...formValues });
    onToggle();
  };

  const baseClassName = 'CompleteGoalModal';
  const { tr } = useTranslation();

  return (
    <WpModal size="sm" isOpen onToggle={onToggle}>
      <WpModalHeader>{tr('perform.goals.complete_modal.header')}</WpModalHeader>
      <WpModalBody>
        <div className={`${baseClassName}`}>
          <div className={`${baseClassName}__title`}>{tr('perform.goals.complete_modal.update_status')}</div>
          <div className={`${baseClassName}__status`}>
            <GoalStatus goal={goal} editable />
          </div>
          <div className={`${baseClassName}__title`}>{tr('perform.goals.complete_modal.record_results')}</div>
          <textarea
            placeholder={tr('perform.goals.complete_modal.placeholder')}
            className={`${baseClassName}__textarea`}
            value={formValues.resolutionNote}
            onChange={handleResolutionNoteChange}
          />
          {goal.numIncompleteActionItems > 0 && (
            <>
              <div className={`${baseClassName}__title`}>
                {tr('perform.goals.complete_modal.close_action_items_question')}
              </div>
              <div className={`${baseClassName}__description`}>
                {tr('perform.goals.complete_modal.open_action_items_description', `${goal.numIncompleteActionItems}`)}
              </div>
              <div>
                <RadioInput
                  checked={formValues.closeActionItems}
                  label={tr('perform.goals.complete_modal.complete_action_items')}
                  onClick={handleCompleteActionItemsClick}
                />
                <RadioInput
                  checked={!formValues.closeActionItems}
                  label={tr('perform.goals.complete_modal.keep_action_items_open')}
                  onClick={handleKeepActionItemsClick}
                />
              </div>
            </>
          )}
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={onToggle}>
            {tr('perform.modal.buttons.cancel')}
          </WpButton>
          <WpButton qaTest="complete-goal-submit-button" onClick={handleCompleteClick}>
            {tr('perform.goals.complete_modal.submit')}
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
}
